import React, { useEffect } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import BatchProgressBar from '../BatchProgressBar';
import { useBatchSignature } from '../../hooks/useBatchSignature';
import { colors, spacing, typography, radius } from '../../theme/tokens';
import { ENV } from '../../config/env';
import { SIGNATURE_CLIENT_MESSAGES as CLIENTE_MSG } from '../../constants/signatureClientMessages';

// Códigos de motivo de fallo por doc (server-side, #101) → texto amigable en castellano. Módulo-scope
// para no recrear el objeto en cada render. Reintentables (PREPARE_ERROR/BLOQUEADO) → ver RETRYABLE_REASONS.
const FAILURE_REASON_LABELS = {
  PREPARE_ERROR: 'No se pudo preparar el documento (problema de almacenamiento en el servidor). Reintentá; si persiste, avisá al administrador.',
  BLOQUEADO: 'El documento estaba bloqueado por otra operación. Reintentá.',
  SIN_PERMISO: 'No tenés permiso para firmar este documento.',
  SERIE_SIN_MASIVA: 'La serie no tiene habilitada la firma masiva.',
  NO_PENDING: 'El documento no está pendiente de firma.',
  YA_FIRMADO: 'El documento ya tiene una firma.',
  // Reintentar no sirve: el archivo almacenado no es el que se registró al importarlo. Requiere
  // intervención (reimportar el documento o revisar qué pasó con el binario). Backlog #124.
  INTEGRIDAD: 'El archivo no coincide con el hash registrado del documento. No se firmó; avisá al administrador.',
  // #108: llegó una firma con menos garantías de las que la serie exige. La causa esperable es un
  // cliente de firma desactualizado, que ignora el nivel pedido y firma todo sin sello. Reintentar
  // con el mismo cliente da lo mismo: hay que actualizarlo.
  NIVEL_INSUFICIENTE: 'La firma no alcanzó el nivel que exige la serie documental (falta el sello de tiempo) '
    + 'y no se publicó. Actualizá el cliente de firma; si ya está actualizado, avisá al administrador.',
  // El cliente de firma no tiene TSA configurada y la serie pide sello de tiempo.
  tsa_no_configurada: 'El cliente de firma no tiene configurado el servicio de sello de tiempo, '
    + 'que esta serie exige. Avisá al administrador.',
  nivel_no_soportado: 'El cliente de firma instalado no puede producir el nivel que exige la serie. '
    + 'Actualizá el cliente o avisá al administrador.',
  // Revocación: sin estos, el rechazo caía en el mensaje genérico y había que leer los logs.
  // Descartes de la preparación: el motor los registra bien, pero sin texto el usuario veía un
  // fallo sin explicación y había que leer la base para saber qué pasó.
  NO_PENDING: 'El documento ya no está pendiente de firma: puede haberse firmado o rechazado '
    + 'después de armar el lote. Actualizá la lista.',
  YA_FIRMADO: 'El documento ya tiene una firma vigente. No se vuelve a firmar.',
  SIN_PERMISO: 'No tenés acceso a la serie documental de este documento.',
  SERIE_SIN_MASIVA: 'La serie documental de este documento no tiene habilitada la firma masiva.',
  SERIE_SIN_INDIVIDUAL: 'La serie documental de este documento no tiene habilitada la firma individual.',
  SERIE_INEXISTENTE: 'La serie documental del documento no existe. Avisá al administrador.',
  BLOQUEADO: 'El documento está tomado por otra sesión de firma. Reintentá en unos minutos.',
  ABANDONADO: 'La sesión de firma de este documento se abandonó antes de completarse.',
  PREPARE_ERROR: 'No se pudo preparar el documento para firmar. Reintentá; si persiste, avisá al administrador.',
  content_too_large: 'La firma no entra en el espacio reservado del documento. Avisá al administrador: '
    + 'hay que ampliar el hueco del placeholder.',
  revocation_revoked: 'El certificado con el que firmaste está REVOCADO. No se puede firmar con él.',
  revocation_unknown: 'No se pudo verificar si el certificado está revocado, y la serie exige una '
    + 'verificación concluyente. Suele pasar con un certificado vencido o cuando el servidor no '
    + 'puede consultar a la autoridad certificante. Avisá al administrador.',
};
/**
 * Cuánto tarda pedir un sello de tiempo a una TSA pública, por documento (#108). La UBA no tiene
 * servidor de sellado propio, así que este costo es real y corre en la máquina del firmante. Se
 * muestra para que una espera larga se entienda como lo que es —la configuración de la serie— y no
 * como el sistema colgado.
 */
const SEGUNDOS_POR_SELLO = 15;

// Motivos transitorios que next-chunk?retryFailed=true reintenta (server resetea a PENDING).
const RETRYABLE_REASONS = ['PREPARE_ERROR', 'BLOQUEADO'];

/**
 * Fase operativa que reporta el server (#103.2 Task E') → qué se le dice al usuario. Antes el modal
 * sólo sabía decir "Firmando…" o "Ensamblando y publicando…" según su propio estado, aunque el
 * server estuviera en otra cosa (preparando, esperando un lease, ingiriendo) — y cuando la espera se
 * estiraba, el usuario no tenía forma de saber si algo pasaba. Ahora el mensaje es el del server.
 */
const PHASE_LABELS = {
  delivering: 'Firmando con el token…',
  preparing: 'Preparando documentos en el servidor…',
  assembling: 'Ensamblando y publicando…',
  waiting_lease: 'Esperando la entrega de documentos…',
  ingesting: 'Resolviendo el lote en el servidor…',
};

/**
 * Modal wizard de firma masiva (F1D-5). Pasos: resumen/consentimiento → PIN → certs → progreso →
 * resultado. Consume useBatchSignature (que corre el loop next-chunk→sign-batch→submit-chunk).
 *
 * @param {boolean} open
 * @param {{docIds?:number[], filter?:object, count:number, resumeBatchId?:number}} selection
 * @param {() => void} onClose
 * @param {() => void} onDone — el lote terminó (el caller refresca la lista)
 */
const BatchSignatureModal = ({ open, selection, onClose, onDone }) => {
  const bs = useBatchSignature({ onDone: () => {} });
  const { state, message, pin, setPin, certs, certAlias, setCertAlias, progress, failures, failuresBreakdown, phase, resumeWithRetry } = bs;

  // Reintentables solo desde el breakdown server-side: los fallos del daemon (failures) no llevan
  // failureReason, así que nunca son "reintentables por retryFailed" (retry del daemon = reanudar el loop).
  const hasRetryableFailures = (failuresBreakdown || []).some((f) => RETRYABLE_REASONS.includes(f.failureReason));

  // Al abrir: reiniciar el estado del hook, y nada más. El flujo NO arranca automáticamente — el
  // usuario debe confirmar en la pantalla de consentimiento/resumen antes de que comience la firma.
  //
  // El reinicio es necesario porque este modal vive MONTADO en la página (sólo se muestra u oculta),
  // así que el estado del lote anterior sobrevive al cierre. Tras una masiva exitosa quedaba en
  // 'done' y al reabrir se veía el progreso del lote viejo con el pie sin ningún botón: una sola
  // firma masiva por sesión de navegador, sin más salida que recargar (GitLab #38). El cierre por
  // error sí reiniciaba —handleClose llama cancel()—, el de éxito no.
  //
  // Se hace acá y no en handleDone para cubrir TODOS los caminos de cierre (botón, backdrop, ESC) y
  // también el caso en que el contenedor deje el modal montado sin cerrarlo. `cancel()` es el
  // reinicio: corta los polls en vuelo, limpia PIN y certificados, y vuelve a 'idle'.
  useEffect(() => {
    if (!open) return;
    bs.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const busy = state === 'checking' || state === 'signing' || state === 'polling' || state === 'awaiting_consent';
  const title = state === 'done' ? 'Firma masiva completada' : 'Firmar documentos';

  const handleClose = () => { bs.cancel(); onClose?.(); };
  const handleDone = () => { onDone?.(); };
  // startOrResume: punto de entrada único para iniciar O reanudar. Lo usan tanto el botón de
  // confirmación inicial como los botones de reintento (daemon_down, consent_timeout), para que
  // un retry en un flujo de resume no llame begin() y pierda el batchId (I1, ADR-033).
  const startOrResume = () => {
    if (selection?.resumeBatchId) bs.resume({ batchId: selection.resumeBatchId, count: selection.count });
    else bs.begin({ docIds: selection?.docIds, filter: selection?.filter, count: selection?.count });
  };
  const handleConfirm = startOrResume;

  return (
    <Modal
      open={open}
      onClose={state === 'done' ? handleDone : handleClose}
      title={title}
      size="md"
      closeOnBackdrop={!busy}
      closeOnEsc={!busy}
      footer={renderFooter()}
    >
      {renderBody()}
    </Modal>
  );

  function renderBody() {
    switch (state) {
      case 'checking':
        return <p style={styles.muted}>Verificando el cliente local de firma…</p>;
      case 'daemon_down':
        return (
          <div style={styles.alertWarn}>
            {CLIENTE_MSG.daemonDown(ENV.APP_NAME)}
            {' '}
            <a
              href={CLIENTE_MSG.clientPagePath}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.alertLink}
            >
              {CLIENTE_MSG.clientPageCta}
            </a>
          </div>
        );
      // #108: por debajo del mínimo el cliente ignora el nivel PAdES y firma todo sin sello. Esas
      // firmas se rechazan al publicarlas, así que arrancar el lote sería quemar una operación del
      // token por documento para nada.
      case 'client_outdated':
        return (
          <div style={styles.alertError}>
            La versión instalada del cliente de firma es anterior a la mínima requerida y no puede
            firmar con las garantías que exigen las series documentales. Actualizalo desde la página
            del cliente de firma y volvé a intentar.
          </div>
        );
      case 'awaiting_consent':
        return (
          <div style={styles.alertWarn}>
            Autorizá este sitio en la ventana del cliente de firma; después continuará automáticamente.
          </div>
        );
      case 'consent_timeout':
        return (
          <div style={styles.alertError}>
            No se recibió la autorización del cliente de firma a tiempo. Abrí la aplicación de firma y reintentá.
          </div>
        );
      case 'idle':
      case 'summary':
        return (
          <div style={styles.alertWarn}>
            Vas a firmar{' '}
            <strong>
              {typeof selection?.count === 'number'
                ? `${selection.count} documento${selection.count === 1 ? '' : 's'}`
                : 'todos los documentos pendientes del filtro seleccionado'}
            </strong>
            {' '}con tu certificado del token. Esta acción no se puede deshacer por documento.
          </div>
        );
      case 'pin':
        return (
          <>
            {message && <div style={styles.alertError}>{message}</div>}
            <label htmlFor="batch-pin" style={styles.label}>PIN del token</label>
            <input
              id="batch-pin"
              type="password"
              autoComplete="off"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') bs.loadCerts(); }}
              placeholder="Ingresá el PIN de tu token"
              style={styles.input}
            />
          </>
        );
      case 'certs':
        return (
          <>
            {message && <div style={styles.alertError}>{message}</div>}
            <p style={{ ...styles.label, marginBottom: spacing[2] }}>Certificado para firmar</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[2] }}>
              {certs.map((c) => (
                <label key={c.alias} style={styles.certRow}>
                  <input type="radio" name="batch-cert" value={c.alias}
                    checked={certAlias === c.alias} onChange={() => setCertAlias(c.alias)} />
                  <span style={{ marginLeft: spacing[2], color: colors.text }}>{c.subject}</span>
                </label>
              ))}
            </div>
          </>
        );
      // #103/ADR-036: mientras el ingest corre server-side, el hook queda en 'resolving' (hasta 3
      // min en lotes grandes). Sin este caso caía en el `default`, que pinta una caja de error roja
      // con `message` vacío: el usuario veía "Ocurrió un error en la firma masiva" durante una
      // espera perfectamente normal. La fase real la reporta el server (`ingesting` casi siempre).
      case 'resolving':
        return (
          <p style={styles.muted}>{PHASE_LABELS[phase] || PHASE_LABELS.ingesting}</p>
        );
      case 'signing':
      case 'polling':
      case 'done': {
        // Feedback UNIFORME con la aprobación/revisión masiva: mismo componente BatchProgressBar.
        // Dos fases con avance propio: 'signing' cuenta los docs firmados por el cliente (chunk a
        // chunk); 'polling'/'done' cuenta los ensamblados+fallidos del server. Sin esto la barra
        // quedaba congelada en 0/N durante toda la firma (el usuario no veía ningún avance).
        const done = state === 'done';
        const signing = state === 'signing';
        const signed = progress.signed || 0;
        const settled = (progress.assembled || 0) + (progress.failed || 0);
        // En 'polling'/'done' la barra NO debe RETROCEDER: al terminar la firma se firmaron N docs
        // (signed=N) pero el ensamblado async arranca en assembled=0 → sin piso, la barra saltaba de
        // N/N a 0/N. Se pisa procesados con `signed` mientras el server ensambla (review Codex #95).
        const procesados = signing ? signed : Math.max(signed, settled);
        const exitosos = signing ? signed : (progress.assembled || 0);
        const fallidos = signing ? 0 : (progress.failed || 0);
        const task = {
          operation: 'SIGNATURE',
          fase: done ? 'COMPLETADO' : 'PROCESANDO',
          running: !done,
          totalDocs: progress.total || 0,
          procesados,
          exitosos,
          fallidos,
          porcentaje: progress.total > 0 ? Math.round((procesados / progress.total) * 100) : 0,
          // #103.2 Task E': la fase del server manda; si no la reporta (server viejo o sin
          // actividad), se cae al mensaje de siempre según el estado local del modal.
          mensaje: done ? undefined
            : (PHASE_LABELS[phase] || (signing ? 'Firmando con el token…' : 'Ensamblando y publicando…')),
          failedDocs: (failures || []).map((f) => ({ idDoc: f.docId, error: f.error })),
        };
        // Fusionar fallos server-side (failuresBreakdown) con los del daemon (failures), sin duplicar por docId.
        const daemonDocIds = new Set((failuresBreakdown || []).map((f) => f.docId));
        const mergedFailures = [
          ...(failuresBreakdown || []),
          ...(failures || []).filter((f) => !daemonDocIds.has(f.docId)),
        ];
        // #108: aviso de costo del sellado. El nivel lo decide la serie y lo resuelve el servidor al
        // preparar, así que este número no existe antes de que empiece la firma — se muestra en el
        // primer momento en que es real, no estimado de antemano. El usuario puede cancelar desde la
        // barra de progreso; lo ya firmado no se pierde.
        const sellados = progress.sellados || 0;
        const minutosEstimados = Math.ceil((sellados * SEGUNDOS_POR_SELLO) / 60);
        return (
          <>
            {signing && sellados > 0 && (
              <div style={styles.alertWarn}>
                <strong>{sellados}</strong> {sellados === 1 ? 'documento lleva' : 'documentos llevan'}
                {' '}sello de tiempo, que se pide a un servicio externo y tarda unos
                {' '}{SEGUNDOS_POR_SELLO} segundos por documento
                {minutosEstimados >= 1 && <> (~{minutosEstimados} min en total)</>}.
                {' '}Es la configuración de la serie documental.
              </div>
            )}
            {signing && <p style={styles.muted}>Firmando… no retires el token.</p>}
            <BatchProgressBar
              task={task}
              onCancel={handleClose}
              onClose={done ? handleDone : handleClose}
            />
            {mergedFailures.length > 0 && (
              <div style={{ marginTop: spacing[3] }}>
                <p style={{ ...styles.label, marginBottom: spacing[1] }}>Documentos con error:</p>
                <ul style={{ margin: 0, paddingLeft: spacing[4], fontSize: typography.fontSize.sm, color: colors.errorDark }}>
                  {mergedFailures.map((f) => (
                    <li key={f.docId}>
                      <strong>Documento #{f.docId}:</strong>{' '}
                      {FAILURE_REASON_LABELS[f.failureReason] || f.error || f.failureReason || 'Error desconocido.'}
                    </li>
                  ))}
                </ul>
                {/* La acción "Reintentar documentos fallidos" vive en el footer (renderFooter, caso 'done'),
                    no acá, para no duplicar el botón. La lista de arriba es solo informativa. */}
              </div>
            )}
          </>
        );
      }
      case 'paused':
      default:
        return <div style={styles.alertError}>{message || 'Ocurrió un error en la firma masiva.'}</div>;
    }
  }

  function renderFooter() {
    if (state === 'idle' || state === 'summary') {
      return (
        <>
          <Button variant="ghost" onClick={handleClose}>Cancelar</Button>
          <Button variant="primary" onClick={handleConfirm}>Confirmar y firmar</Button>
        </>
      );
    }
    if (state === 'client_outdated') {
      // Sin "Reintentar": con el mismo cliente instalado el resultado sería idéntico.
      return <Button variant="ghost" onClick={handleClose}>Cerrar</Button>;
    }
    if (state === 'daemon_down') {
      return (
        <>
          <Button variant="ghost" onClick={handleClose}>Cerrar</Button>
          <Button variant="primary" onClick={startOrResume}>Reintentar</Button>
        </>
      );
    }
    if (state === 'consent_timeout') {
      return (
        <>
          <Button variant="ghost" onClick={handleClose}>Cerrar</Button>
          <Button variant="primary" onClick={startOrResume}>Reintentar</Button>
        </>
      );
    }
    if (state === 'pin') {
      return (
        <>
          <Button variant="ghost" onClick={handleClose}>Cancelar</Button>
          <Button variant="primary" onClick={bs.loadCerts} disabled={!pin}>Continuar</Button>
        </>
      );
    }
    if (state === 'certs') {
      return (
        <>
          <Button variant="ghost" onClick={handleClose}>Cancelar</Button>
          <Button variant="primary" onClick={bs.run}>Firmar {selection?.count ?? 'todos'}</Button>
        </>
      );
    }
    if (state === 'done') {
      if (hasRetryableFailures) {
        return (
          <>
            <Button variant="ghost" onClick={handleDone}>Cerrar</Button>
            <Button variant="primary" onClick={() => resumeWithRetry()}>Reintentar documentos fallidos</Button>
          </>
        );
      }
      return null; // BatchProgressBar ya muestra el cierre (uniforme con aprobación/revisión).
    }
    if (state === 'paused') {
      return (
        <>
          <Button variant="ghost" onClick={handleClose}>Cerrar</Button>
          <Button variant="primary" onClick={bs.retry}>Reintentar</Button>
        </>
      );
    }
    // signing/polling: BatchProgressBar ya muestra el botón Cancelar (uniforme con
    // aprobación/revisión). El batch es reanudable y el server sigue en background.
    return null;
  }
};

const styles = {
  muted: { margin: 0, marginBottom: spacing[2], color: colors.textMuted },
  label: {
    display: 'block', marginBottom: spacing[1],
    fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: colors.text,
  },
  input: {
    width: '100%', boxSizing: 'border-box', padding: spacing[2],
    fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.sans, color: colors.text,
    border: `1px solid ${colors.borderStrong}`, borderRadius: radius.sm,
  },
  certRow: {
    display: 'flex', alignItems: 'center', padding: spacing[2],
    border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer',
    fontSize: typography.fontSize.base,
  },
  alertError: {
    padding: spacing[2], marginBottom: spacing[3], borderRadius: radius.sm,
    backgroundColor: colors.errorBg, color: colors.errorDark, fontSize: typography.fontSize.sm,
  },
  alertWarn: {
    padding: spacing[3], borderRadius: radius.sm,
    backgroundColor: colors.warningBg, color: colors.warningDark, fontSize: typography.fontSize.base,
  },
  // El enlace se abre en otra pestaña a propósito: el usuario descarga e instala el cliente sin
  // perder el modal del lote, y vuelve a apretar "Reintentar".
  alertLink: { color: 'inherit', fontWeight: typography.fontWeight.semibold, textDecoration: 'underline' },
  alertOk: {
    padding: spacing[3], marginBottom: spacing[2], borderRadius: radius.sm,
    backgroundColor: colors.successBg, color: colors.successDark, fontSize: typography.fontSize.base,
  },
};

export default BatchSignatureModal;
